import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { images } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dateUtils';

const SORT_OPTIONS = [
  { value: 'purchase_date', label: 'Purchase Date' },
  { value: 'pop_status', label: 'Population Status' },
  { value: 'current_population', label: 'Current Population' },
  { value: 'current_pop_higher', label: 'Higher Grades' }
];

const PopulationReportChart = ({ data }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('purchase_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [imageUrls, setImageUrls] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [threshold, setThreshold] = useState(20);

  useEffect(() => {
    const updateThreshold = () => {
      if (window.innerWidth >= 1280) setThreshold(20); // xl:grid-cols-4
      else if (window.innerWidth >= 1024) setThreshold(15); // lg:grid-cols-3
      else if (window.innerWidth >= 640) setThreshold(10); // sm:grid-cols-2
      else setThreshold(5); // grid-cols-1
    };

    updateThreshold();
    window.addEventListener('resize', updateThreshold);
    return () => window.removeEventListener('resize', updateThreshold);
  }, []);

  // Ensure data is an array
  const coins = Array.isArray(data) ? data : data?.rows || [];

  useEffect(() => {
    const loadImages = async () => {
      const urls = {};
      try {
        for (const coin of coins) {
          if (coin.thumbnail_path) {
            const imageUrl = await images.getImage(coin.thumbnail_path, coin.source);
            if (imageUrl) {
              urls[coin.certification_number] = imageUrl;
            }
          }
        }
        setImageUrls(urls);
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };
    
    if (coins.length > 0) {
      loadImages();
    }
  }, [coins]);
  
  if (!coins || coins.length === 0) {
    return <div className="text-gray-500">No population data available</div>;
  }

  const handleSortChange = (e) => {
    if (e.target.value === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(e.target.value);
      setSortDirection('asc');
    }
  };

  const sortedData = [...coins].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle special sort cases
    if (sortField === 'pop_status') {
      aValue = a.pop_higher_when_bought === 0 ? (a.current_pop_higher === 0 ? 1 : 0) : -1;
      bValue = b.pop_higher_when_bought === 0 ? (b.current_pop_higher === 0 ? 1 : 0) : -1;
    }

    if (sortDirection === 'desc') {
      [aValue, bValue] = [bValue, aValue];
    }

    return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Population Reports</h2>
        <select
          id="population-sort"
          name="population-sort"
          value={sortField}
          onChange={handleSortChange}
          className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        >
          {SORT_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              Sort by {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${!showAll ? 'max-h-[700px] overflow-hidden' : ''}`}>
          {sortedData.map((coin) => {
          const popHigherWhenBought = coin.pop_higher_when_bought ?? 0;
          const currentPopHigher = coin.current_pop_higher ?? 0;
          const wasTopPop = popHigherWhenBought === 0 && coin.population_when_bought > 0;
          const isStillTopPop = wasTopPop && currentPopHigher === 0 && coin.current_population > 0;
          const popChange = coin.current_population - coin.population_when_bought;
          const popHigherChange = currentPopHigher - popHigherWhenBought;

          return (
            <div 
              key={coin.id}
              onClick={() => navigate(`/coins/${coin.id}`)}
              className={`rounded-lg border ${
                wasTopPop 
                  ? isStillTopPop 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-red-500 bg-red-50'
                  : 'border-gray-200'
              } overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-200`}
            >
              <div className="flex items-start p-1.5 border-b border-gray-200">
                {coin.thumbnail_path ? (
                  <img 
                    src={imageUrls[coin.certification_number] || '/images/placeholder.svg'}
                    alt={coin.variety || coin.certification_number}
                    className="w-14 h-14 object-cover rounded"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">
                    No img
                  </div>
                )}
                <div className="ml-3 flex-1">
                  <div className="font-medium">
                    {[coin.year, coin.denomination, coin.grade]
                      .filter(Boolean)
                      .join(' ')}
                  </div>
                  <div className="text-xs text-gray-600">
                    Cert #{coin.certification_number}
                  </div>
                  {wasTopPop && (
                    <div className={`text-xs font-medium ${
                      isStillTopPop ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {isStillTopPop ? '✨ Top Pop' : '❌ Was Top Pop'}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Added {formatDate(coin.purchase_date, { user })}
                  </div>
                </div>
              </div>

              <div className="p-1.5 space-y-0.5 text-sm">
                <div className="flex justify-between">
                  <span>Population:</span>
                  <span className={`font-medium ${popChange > 0 ? 'text-red-600' : popChange < 0 ? 'text-green-600' : ''}`}>
                    {coin.current_population}
                    {popChange !== 0 && (
                      <span className="ml-1 text-xs">
                        ({popChange > 0 ? '+' : ''}{popChange})
                      </span>
                    )}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Higher:</span>
                  <span className={`font-medium ${popHigherChange > 0 ? 'text-red-600' : popHigherChange < 0 ? 'text-green-600' : ''}`}>
                    {currentPopHigher}
                    {popHigherChange !== 0 && (
                      <span className="ml-1 text-xs">
                        ({popHigherChange > 0 ? '+' : ''}{popHigherChange})
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          );
          })}
          </div>
          {!showAll && (
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
          )}
        </div>
        
        {coins.length > threshold && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setShowAll(prev => !prev)}
              className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline bg-white"
            >
              {showAll ? 'Show Less' : 'Show All'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PopulationReportChart;
